import React, { useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, BarChart3 } from "lucide-react";
import { fmtMoney } from "@/components/DataTable";

const REPORTS = [
  { key: "workload", label: "Workload (Cases by Owner)" },
  { key: "renewals", label: "Renewals by Outcome" },
  { key: "pipeline", label: "Pipeline by Stage" },
  { key: "carriers", label: "Carrier Results" },
  { key: "book", label: "Book of Business by Line" },
  { key: "claims", label: "Claims by Status" },
  { key: "data_quality", label: "Data Quality" },
];

export default function Reports() {
  const [active, setActive] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async (r) => {
    setActive(r.key); setLoading(true); setReport(null);
    try { const { data } = await api.get(`/reports/${r.key}`); setReport({ ...data, label: r.label }); }
    catch { toast.error("Could not run report."); }
    finally { setLoading(false); }
  };

  const exportCsv = () => {
    if (!report) return;
    const lines = [report.columns.join(",")];
    report.rows.forEach((row) => lines.push(row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${active}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const isMoney = (c) => /premium|pipeline/i.test(c);

  return (
    <div className="p-6 animate-fade-in">
      <PageHeader title="Reports" subtitle="Operational reporting calculated live from agency records." />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          {REPORTS.map((r) => (
            <button key={r.key} data-testid={`report-${r.key}`} onClick={() => run(r)}
              className={`w-full text-left px-4 py-3 border rounded-sm nav-link flex items-center gap-2 ${active === r.key ? "bg-navy text-white border-navy" : "bg-white border-border text-slate hover:bg-ice"}`}>
              <BarChart3 className="h-4 w-4" /> {r.label}
            </button>
          ))}
        </div>
        <div className="md:col-span-2">
          {!report && !loading && <div className="bg-white border border-border rounded-sm p-10 text-center text-mutedsea">Select a report to run.</div>}
          {loading && <div className="bg-white border border-border rounded-sm p-10 text-center text-mutedsea">Calculating…</div>}
          {report && (
            <div className="bg-white border border-border rounded-sm">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-cloud">
                <h3 className="font-serif text-xl text-navy">{report.label}</h3>
                <Button size="sm" variant="outline" data-testid="export-csv" onClick={exportCsv} className="border-atlantic text-atlantic"><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
              </div>
              <div className="overflow-x-auto end-scroll">
                <table className="w-full text-sm">
                  <thead><tr className="bg-cloud border-b border-border">{report.columns.map((c) => <th key={c} className="text-left px-3 py-2 text-xs uppercase tracking-wide text-navy font-semibold">{c}</th>)}</tr></thead>
                  <tbody>
                    {report.rows.length === 0 ? <tr><td colSpan={report.columns.length} className="px-3 py-6 text-center text-mutedsea">No data.</td></tr> :
                      report.rows.map((row, i) => (
                        <tr key={i} className="border-b border-border last:border-0">
                          {row.map((cell, j) => <td key={j} className="px-3 py-2">{isMoney(report.columns[j]) ? fmtMoney(cell) : cell}</td>)}
                        </tr>))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
